import { useEffect, useState } from "react";
import { CheckCircle, AlertTriangle, Info, X, AlertCircle } from "lucide-react";
import useGlobalStore, { type ToastData } from "@/store/globalStore";

const Toast: React.FC = () => {
  const { toast, clearToast } = useGlobalStore();
  const [visible, setVisible] = useState(false);
  const [currentToast, setCurrentToast] = useState<ToastData | null>(null);

  useEffect(() => {
    if (toast) {
      setCurrentToast(toast);
      setVisible(true);

      const duration = toast.duration || 4000;
      const timeout = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timeout);
    }
  }, [toast]);

  const handleClose = () => {
    setVisible(false);
    // Clear after animation completes
    setTimeout(() => {
      clearToast();
      setCurrentToast(null);
    }, 300);
  };

  // The action handler almost always raises its own toast when it finishes, so
  // this one is torn down immediately (rather than via handleClose's delayed
  // clear, which would race the replacement toast and wipe it) and the handler
  // runs on the next tick.
  const handleAction = () => {
    const action = currentToast?.action;
    if (!action) return;
    setVisible(false);
    setCurrentToast(null);
    clearToast();
    setTimeout(() => {
      void action.onClick();
    }, 0);
  };

  const getActionStyles = (type: string) => {
    switch (type) {
      case "success":
        return "text-green-800 border-green-400 hover:bg-green-100 focus:ring-green-500";
      case "error":
        return "text-red-800 border-red-400 hover:bg-red-100 focus:ring-red-500";
      case "warning":
        return "text-yellow-800 border-yellow-400 hover:bg-yellow-100 focus:ring-yellow-500";
      case "info":
        return "text-blue-800 border-blue-400 hover:bg-blue-100 focus:ring-blue-500";
      default:
        return "text-gray-800 border-gray-400 hover:bg-gray-100 focus:ring-gray-500";
    }
  };

  if (!currentToast) return null;

  const getToastStyles = (type: string) => {
    const baseStyles = "border rounded-lg shadow-lg backdrop-blur-sm";

    switch (type) {
      case "success":
        return `${baseStyles} bg-green-50 border-green-200 text-green-800`;
      case "error":
        return `${baseStyles} bg-red-50 border-red-200 text-red-800`;
      case "warning":
        return `${baseStyles} bg-yellow-50 border-yellow-200 text-yellow-800`;
      case "info":
        return `${baseStyles} bg-blue-50 border-blue-200 text-blue-800`;
      default:
        return `${baseStyles} bg-gray-50 border-gray-200 text-gray-800`;
    }
  };

  const getIcon = (type: string) => {
    const iconProps = { size: 20, className: "flex-shrink-0" };

    switch (type) {
      case "success":
        return (
          <CheckCircle
            {...iconProps}
            className={`${iconProps.className} text-green-600`}
          />
        );
      case "error":
        return (
          <AlertCircle
            {...iconProps}
            className={`${iconProps.className} text-red-600`}
          />
        );
      case "warning":
        return (
          <AlertTriangle
            {...iconProps}
            className={`${iconProps.className} text-yellow-600`}
          />
        );
      case "info":
        return (
          <Info
            {...iconProps}
            className={`${iconProps.className} text-blue-600`}
          />
        );
      default:
        return <Info {...iconProps} />;
    }
  };

  const getProgressBarColor = (type: string) => {
    switch (type) {
      case "success":
        return "bg-green-400";
      case "error":
        return "bg-red-400";
      case "warning":
        return "bg-yellow-400";
      case "info":
        return "bg-blue-400";
      default:
        return "bg-gray-400";
    }
  };

  return (
    <>
      <div className="fixed top-4 right-4 z-[9999] pointer-events-none">
        <div
          className={`
            pointer-events-auto w-96 max-w-sm
            transform transition-all duration-300 ease-in-out
            ${
              visible
                ? "translate-x-0 opacity-100 scale-100"
                : "translate-x-full opacity-0 scale-95"
            }
          `}
        >
          <div
            className={`${getToastStyles(
              currentToast.type,
            )} p-4 relative overflow-hidden`}
          >
            <div className="absolute bottom-0 left-0 h-1 w-full bg-black bg-opacity-10">
              <div
                className={`h-full ${getProgressBarColor(
                  currentToast.type,
                )} animate-shrink`}
                style={{
                  animationDuration: `${currentToast.duration || 4000}ms`,
                }}
              />
            </div>

            <div className="flex items-start gap-3">
              {getIcon(currentToast.type)}

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-5">
                  {currentToast.message}
                </p>
                {currentToast.action ? (
                  <button
                    type="button"
                    onClick={handleAction}
                    className={`mt-2 px-3 py-1 text-sm font-semibold border rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${getActionStyles(
                      currentToast.type,
                    )}`}
                  >
                    {currentToast.action.label}
                  </button>
                ) : null}
              </div>

              <button
                onClick={handleClose}
                className={`
                  flex-shrink-0 p-1 rounded-md transition-colors duration-200
                  hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2
                  ${
                    currentToast.type === "success"
                      ? "focus:ring-green-500"
                      : ""
                  }
                  ${currentToast.type === "error" ? "focus:ring-red-500" : ""}
                  ${
                    currentToast.type === "warning"
                      ? "focus:ring-yellow-500"
                      : ""
                  }
                  ${currentToast.type === "info" ? "focus:ring-blue-500" : ""}
                `}
                aria-label="Close notification"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Toast;
